<template>
  <div class="flex flex-col w-[300px]">
    <div v-if="fieldType === 'datetime'">
      <div v-show="!showDateTimePicker" class="flex flex-col p-2">
        <div
          v-for="(option, index) in dateTimeOptions"
          :key="index"
          class="flex flex-row items-center gap-2 py-2 px-3 rounded-lg transition-colors duration-200 cursor-pointer flex-shrink-0 justify-between hover:bg-white/[0.08]"
          :class="{ 'bg-white/10': isDateTimeSelected(option) }"
          @click="handleDateTimeOptionClick(option)"
        >
          <div class="flex items-center gap-3">
            <ThemeImage
              v-if="isDateTimeSelected(option)"
              class="size-4 shrink-0"
              :icon="imageNames.TICK_GREY"
              alt="check_icon"
            />
            <div v-else class="size-4 shrink-0" />

            <span class="text_color text-sm font-normal leading-[17px]">
              {{ option.label }}
            </span>
          </div>

          <span
            v-if="option.date"
            class="secondary_color text-xs font-normal leading-[17px]"
          >
            {{ formatDateTimePreview(option.date) }}
          </span>
        </div>
      </div>

      <div v-show="showDateTimePicker" class="flex flex-col p-2">
        <a-date-picker
          :value="customDateTime"
          :show-time="{ format: 'HH:mm' }"
          format="DD MMM YYYY HH:mm"
          placeholder="Select date and time"
          class="w-full datetime_picker_inline"
          :open="showDateTimePicker"
          @change="handleDateTimeChange"
          @openChange="handleDateTimePickerOpenChange"
        />
      </div>
    </div>

    <template v-else>
      <div v-show="!showDatePicker" class="flex flex-col p-2">
        <div
          v-for="(option, index) in dateOptions"
          :key="index"
          class="flex flex-row items-center gap-2 py-2 px-3 rounded-lg transition-colors duration-200 cursor-pointer flex-shrink-0 justify-between hover:bg-white/[0.08]"
          :class="{ 'bg-white/10': isSelected(option) }"
          @click="handleOptionClick(option)"
        >
          <div class="flex items-center gap-3">
            <ThemeImage
              v-if="isSelected(option)"
              class="size-4 shrink-0"
              :icon="imageNames.TICK_GREY"
              alt="check_icon"
            />
            <div v-else class="size-4 shrink-0" />

            <span class="text_color text-sm font-normal leading-[17px]">
              {{ option.label }}
            </span>
          </div>

          <span
            v-if="option.date"
            class="secondary_color text-xs font-normal leading-[17px]"
          >
            {{ formatDatePreview(option.date) }}
          </span>
        </div>
      </div>

      <div v-show="showDatePicker" class="flex flex-col date_picker_container">
        <a-calendar
          v-if="showDatePicker"
          v-model:value="customDate"
          :fullscreen="false"
          @select="handleDateSelect"
          @panelChange="onPanelChange"
        />
      </div>
    </template>
  </div>
</template>

<script setup>
import { ref, computed } from "vue";
import { Calendar as ACalendar, DatePicker as ADatePicker } from "ant-design-vue";
import ThemeImage from "@/src/components/ThemeImage.vue";
import { imageNames } from "@/src/constants/imageConstants.js";
import { getDateOptions } from "@/src/constants/taskConstants.js";
import { getDateTimeOptions } from "@/src/constants/crmConstants.js";
import dayjs from "dayjs";

const props = defineProps({
  selectedValue: {
    type: [String, Object],
    default: null,
  },
  fieldType: {
    type: String,
    default: "date",
  },
});

const emit = defineEmits(["select"]);

const showDatePicker = ref(false);
const customDate = ref(dayjs());
const showDateTimePicker = ref(false);
const customDateTime = ref(dayjs());

const dateTimeOptions = computed(() => {
  try {
    return getDateTimeOptions();
  } catch (error) {
    console.error("Error getting date time options:", error);
    return [];
  }
});

const formatDateTimePreview = (date) => {
  if (!date) return "";
  try {
    const parsedDate = dayjs(date);
    if (!parsedDate.isValid()) {
      console.warn("Invalid date provided to formatDateTimePreview:", date);
      return "";
    }
    return parsedDate.format("ddd DD MMM");
  } catch (error) {
    console.error("Error formatting date time preview:", error);
    return "";
  }
};

const isDateTimeSelected = (option) => {
  try {
    if (option.value === null) {
      if (!props.selectedValue) return true;
      const selectedVal = props.selectedValue?.value ?? props.selectedValue?.date ?? props.selectedValue;
      return selectedVal === null;
    }
    if (!props.selectedValue) return false;
    if (option.value === "custom") return false;

    const rawValue = props.selectedValue?.value || props.selectedValue?.date || props.selectedValue;

    const selectedDate = dayjs.isDayjs(rawValue)
      ? rawValue
      : dayjs(rawValue);

    if (!selectedDate.isValid()) {
      console.warn("Invalid selectedValue in isDateTimeSelected:", props.selectedValue);
      return false;
    }

    const optionDate = dayjs(option.value);
    if (!optionDate.isValid()) {
      console.warn("Invalid option value in isDateTimeSelected:", option.value);
      return false;
    }

    return selectedDate.isSame(optionDate, "day");
  } catch (error) {
    console.error("Error in isDateTimeSelected:", error);
    return false;
  }
};

const handleDateTimeOptionClick = (option) => {
  try {
    if (!option) {
      console.error("No option provided to handleDateTimeOptionClick");
      return;
    }

    if (option.value === "custom") {
      showDateTimePicker.value = true;
      if (props.selectedValue && dayjs.isDayjs(props.selectedValue)) {
        customDateTime.value = props.selectedValue;
      } else if (props.selectedValue) {
        const rawValue = props.selectedValue?.value || props.selectedValue?.date || props.selectedValue;
        const parsedDate = dayjs.isDayjs(rawValue) ? rawValue : dayjs(rawValue);
        customDateTime.value = parsedDate.isValid() ? parsedDate : dayjs();
        if (!parsedDate.isValid()) {
          console.warn("Invalid selectedValue, using current date:", props.selectedValue);
        }
      } else {
        customDateTime.value = dayjs();
      }
    } else {
      if (isDateTimeSelected(option)) {
        emit("select", { value: null, label: null, date: null });
      } else {
        emit("select", { value: option.value, label: option.label, date: option.date });
      }
    }
  } catch (error) {
    console.error("Error in handleDateTimeOptionClick:", error);
    customDateTime.value = dayjs();
  }
};

const handleDateTimeChange = (date) => {
  try {
    if (date) {
      const parsedDate = dayjs.isDayjs(date) ? date : dayjs(date);
      if (!parsedDate.isValid()) {
        console.error("Invalid date in handleDateTimeChange:", date);
        emit("select", { value: null, label: null, date: null });
        return;
      }
      emit("select", { value: parsedDate, label: parsedDate.format("DD MMM YYYY HH:mm"), date: parsedDate });
      showDateTimePicker.value = false;
    } else {
      emit("select", { value: null, label: null, date: null });
    }
  } catch (error) {
    console.error("Error in handleDateTimeChange:", error);
    emit("select", { value: null, label: null, date: null });
  }
};

const handleDateTimePickerOpenChange = (open) => {
  if (!open) {
    showDateTimePicker.value = false;
  }
};

const dateOptions = computed(() => {
  try {
    return getDateOptions();
  } catch (error) {
    console.error("Error getting date options:", error);
    return [];
  }
});

const formatDatePreview = (date) => {
  if (!date) return "";
  try {
    const parsedDate = dayjs(date);
    if (!parsedDate.isValid()) {
      console.warn("Invalid date provided to formatDatePreview:", date);
      return "";
    }
    return parsedDate.format("ddd DD MMM");
  } catch (error) {
    console.error("Error formatting date preview:", error);
    return "";
  }
};

const isSelected = (option) => {
  if (!props.selectedValue) {
    return option.value === null;
  }

  const selectedDateStr =
    typeof props.selectedValue === "string"
      ? props.selectedValue
      : props.selectedValue?.value || props.selectedValue?.date;

  return selectedDateStr === option.value;
};

const handleOptionClick = (option) => {
  try {
    if (!option) {
      console.error("No option provided to handleOptionClick");
      return;
    }

    if (option.value === "custom") {
      showDatePicker.value = true;

      if (props.selectedValue) {
        let parsedDate;
        if (typeof props.selectedValue === "string") {
          parsedDate = dayjs(props.selectedValue);
        } else if (props.selectedValue?.date) {
          parsedDate = dayjs(props.selectedValue.date);
        } else if (props.selectedValue?.value) {
          parsedDate = dayjs(props.selectedValue.value);
        } else {
          parsedDate = dayjs();
        }

        if (!parsedDate.isValid()) {
          console.warn("Invalid selectedValue in handleOptionClick, using current date:", props.selectedValue);
          customDate.value = dayjs();
        } else {
          customDate.value = parsedDate;
        }
      } else {
        customDate.value = dayjs();
      }
    } else {
      if (isSelected(option)) {
        emit("select", { value: null, label: null, date: null });
      } else {
        emit("select", option);
      }
    }
  } catch (error) {
    console.error("Error in handleOptionClick:", error);
    customDate.value = dayjs();
  }
};

const handleDateSelect = (date, info) => {
  try {
    if (date) {
      const parsedDate = dayjs.isDayjs(date) ? date : dayjs(date);
      if (!parsedDate.isValid()) {
        console.error("Invalid date in handleDateSelect:", date);
        return;
      }

      customDate.value = parsedDate;
      if (info?.source === "date") {
        const dateOption = {
          value: parsedDate.format("YYYY-MM-DD"),
          label: parsedDate.format("DD MMM YYYY"),
          date: parsedDate,
        };
        emit("select", dateOption);
        showDatePicker.value = false;
      }
    }
  } catch (error) {
    console.error("Error in handleDateSelect:", error);
  }
};
const onPanelChange = (value) => {
  try {
    if (!value) {
      console.warn("No value provided to onPanelChange");
      return;
    }
    const parsedDate = dayjs.isDayjs(value) ? value : dayjs(value);
    if (!parsedDate.isValid()) {
      console.error("Invalid date in onPanelChange:", value);
      return;
    }
    customDate.value = parsedDate;
  } catch (error) {
    console.error("Error in onPanelChange:", error);
  }
};
</script>

<style scoped>
:deep(.ant-picker-calendar-header .ant-picker-calendar-mode-switch) {
  display: none !important;
}

:deep(.ant-picker-calendar-header) {
  justify-content: space-between !important;
}

:deep(.datetime_picker_inline .ant-picker-input > input) {
  font-size: 13px;
  color: var(--text-color);
}

:deep(.datetime_picker_inline .ant-picker-input > input::placeholder) {
  color: var(--placeholder-text-color);
}
</style>